import { useState, useEffect } from "react";

import { json, redirect } from "@remix-run/node";
import type { ActionFunction, LoaderFunction } from "@remix-run/node";
import {
    Form,
    Link,
    useActionData,
    useLocation,
    useNavigation,
} from "@remix-run/react";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import {
    Copyright,
    DoorOpen,
    Dot,
    FileText,
    Glasses,
    LogOut,
    Star,
    User,
    Users,
} from "lucide-react";

import AuthLayout from "~/components/auth-layout";
import Logo from "~/components/logo";
import { Button } from "~/components/ui/button";
import { P } from "~/components/ui/typography";
import { useToast } from "~/components/ui/use-toast";
import { withCors } from "~/config/cors";
import { useLogoutOverlay } from "~/context/LogoutOverlayContext";
import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { useAnonymous } from "~/hooks/use-anonymous";
import { useUTMCapture } from "~/hooks/use-utm-capture";
import { getUnauditableSystemAdminSupabaseClient } from "~/integrations/supabase/client.server";
import { createSupabaseServerClient } from "~/integrations/supabase/ssr.server";
import { trackEvent } from "~/lib/analytics";
import { SUPPORT_CONTACT_EMAIL } from "~/lib/branding";
import { handleLogout } from "~/lib/logout";
import { useIsPractitioner } from "~/lib/user";
import { logAnonymousPageView } from "~/modules.server/analytics/anonymous-page-view.server";
import { getLogger } from "~/modules.server/logger";
import { profileRepository } from "~/modules.server/profile/repository";
import { pageMeta } from "~/utils/meta";

const Landing = () => {
    const { toast } = useToast();
    const location = useLocation();
    const navigation = useNavigation();
    const actionData = useActionData<{ error?: string }>();
    const { isAnonymous } = useAnonymous();
    const [hasTrackedAnonymous, setHasTrackedAnonymous] = useState(false);
    const [isProcessingLogout, setIsProcessingLogout] = useState(false);
    const { isPractitioner, isLoading: isPractitionerLoading } =
        useIsPractitioner();

    // AIDEV-NOTE: Capture UTM attribution from URL on first arrival
    // Must be client-side because we need document.referrer (not available in loader)
    // Attribution is stored in sessionStorage and retrieved during signup
    useUTMCapture();

    // AIDEV-NOTE: Use session from context instead of local state - ensures sync with server
    const { userId, loading: isLoadingSession } = useSupabaseSession();
    const isAuthenticated = !!userId;
    // AIDEV-NOTE: Include isPractitionerLoading to prevent button layout shift
    // Without this, isPractitioner shows false briefly until the query settles
    const isLoading = isLoadingSession || isPractitionerLoading;
    const isSubmittingAnonymous =
        navigation.state === "submitting" &&
        navigation.formData?.get("intent") === "anonymous-login";
    const { showLogoutOverlay } = useLogoutOverlay();
    const handleLandingLogout = async () => {
        if (isProcessingLogout) return;

        setIsProcessingLogout(true);
        showLogoutOverlay();
        try {
            await handleLogout();
        } finally {
            // Location change interrupts state update, but keep for tests / fallback
            setIsProcessingLogout(false);
        }
    };

    // Handle action errors
    useEffect(() => {
        if (actionData?.error) {
            toast({
                title: "Error",
                description: actionData.error,
                variant: "destructive",
            });
        }
    }, [actionData, toast]);

    // AIDEV-NOTE: Screen tracking handled by AnalyticsScreenTracker component in root.tsx
    // No need for manual tracking here

    // Track anonymous signup after successful authentication
    useEffect(() => {
        if (isAuthenticated && isAnonymous && !hasTrackedAnonymous) {
            trackEvent("anonymous signup", {});
            setHasTrackedAnonymous(true);
        }
    }, [isAuthenticated, isAnonymous, hasTrackedAnonymous]);

    if (isLoading) {
        return (
            <AuthLayout title="">
                <div className="flex items-center justify-center">
                    <div className="text-white">Loading...</div>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            logoVariant="horizontal"
            promotionalBackgroundImage="/testimonial-background.png"
            promotionalContent={
                <div className="max-w-lg space-y-6 text-center">
                    <div className="mb-4 flex justify-center">
                        {[...Array(5)].map((_, idx) => (
                            <Star
                                key={idx}
                                size={28}
                                fill="#FB923C"
                                stroke="#FB923C"
                                style={{
                                    marginLeft: idx === 0 ? 0 : "0.25rem",
                                }}
                            />
                        ))}
                    </div>
                    <h2 className="font-display text-brand-forest-800 text-4xl font-bold leading-relaxed">
                        The Place for Your Parts Work
                    </h2>
                    <P className="text-gray-700 text-lg">
                        Whether you're a client or a practitioner, Parts
                        Companion is the home for your parts work. Record,
                        reflect, and grow with every session.
                    </P>
                </div>
            }
        >
            <div className="space-y-8 text-center">
                <p className="text-center text-white/90">
                    Clarity, continuity, and connection in your IFS journey.
                </p>
                <div className="flex flex-col items-stretch space-y-4">
                    {isAuthenticated ? (
                        <>
                            {isPractitioner && (
                                <Link to="/practitioner">
                                    <Button
                                        className="text-md flex h-12 w-full px-8"
                                        variant="onDark"
                                    >
                                        <Users className="mr-2 size-5" />
                                        My Clients
                                    </Button>
                                </Link>
                            )}

                            <Link to="/sessions">
                                <Button
                                    className="text-md flex h-12 w-full px-8"
                                    variant="onDark"
                                >
                                    <FileText className="mr-2 size-5" />
                                    My Sessions
                                </Button>
                            </Link>

                            {isAnonymous && (
                                <Link to="/link-account">
                                    <Button
                                        className="text-md flex h-12 w-full px-8"
                                        variant="onDark"
                                    >
                                        <User className="mr-2 size-5" />
                                        Create an account
                                    </Button>
                                </Link>
                            )}

                            {isAnonymous && (
                                <Link to={"/login" + location.search}>
                                    <Button
                                        className="text-md flex h-12 w-full px-8"
                                        variant="onDark"
                                    >
                                        <User className="mr-2 size-5" />
                                        Log In
                                    </Button>
                                </Link>
                            )}

                            {!isAnonymous && (
                                <>
                                    <Button
                                        type="button"
                                        onClick={handleLandingLogout}
                                        className="text-md flex h-12 w-full px-8"
                                        variant="secondary"
                                        isLoading={isProcessingLogout}
                                    >
                                        <LogOut className="mr-2 size-5" />
                                        Log Out
                                    </Button>
                                    {/* AIDEV-NOTE: Always-available login escape hatch.
                                        In standalone PWAs the client session (localStorage) can
                                        read as authenticated while the server cookie is gone, so
                                        the page lands here showing only "Log Out". This link keeps
                                        login reachable in that desynced state. */}
                                    <Link
                                        to={"/login" + location.search}
                                        className="flex items-center justify-center gap-2 text-brand-cream-400 hover:text-brand-cream-300"
                                    >
                                        <DoorOpen className="size-5" />
                                        Log into a different account
                                    </Link>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {/*
                            <Form method="post">
                                <input
                                    type="hidden"
                                    name="intent"
                                    value="anonymous-login"
                                />
                                <input
                                    type="hidden"
                                    name="redirectTo"
                                    value={
                                        "/onboarding/terms" + location.search
                                    }
                                />
                                <Button
                                    type="submit"
                                    className="text-md h-12 w-full px-8"
                                    variant="onDark"
                                    isLoading={isSubmittingAnonymous}
                                >
                                    <Glasses className="mr-2 size-5" />
                                    Try anonymously
                                </Button>
                            </Form>
                                */}

                            <Link to={"/signup" + location.search}>
                                <Button
                                    className="text-md h-12 w-full px-8"
                                    variant="onDark"
                                >
                                    <User className="mr-2 size-5" />
                                    Create an account
                                </Button>
                            </Link>
                            <Link
                                to={"/login" + location.search}
                                className="flex items-center justify-center gap-2 text-brand-cream-400 hover:text-brand-cream-300"
                            >
                                <DoorOpen className="size-5" />
                                Log into existing account
                            </Link>
                        </>
                    )}
                </div>

                <footer className="space-y-2 text-xs font-medium text-white/80">
                    <div className="mb-10 flex justify-center gap-1">
                        <Link to="/privacy">
                            <button
                                type="button"
                                className="text-brand-cream-400 hover:text-brand-cream-300"
                            >
                                Privacy Policy
                            </button>
                        </Link>
                        <Dot className="size-4" />
                        <Link to="/terms">
                            <button
                                type="button"
                                className="text-brand-cream-400 hover:text-brand-cream-300"
                            >
                                Terms of Use
                            </button>
                        </Link>
                        <Dot className="size-4" />
                        <Link to={`mailto:${SUPPORT_CONTACT_EMAIL}`}>
                            <button
                                type="button"
                                className="text-brand-cream-400 hover:text-brand-cream-300"
                            >
                                Support
                            </button>
                        </Link>
                    </div>
                    <P className="flex items-center justify-center text-center text-xs text-white/60">
                        <Copyright className="mr-1 size-4" /> 2025 Evolve Labs
                        Inc.
                    </P>
                </footer>
            </div>
        </AuthLayout>
    );
};

export const meta = pageMeta(
    "Parts Companion",
    "Clarity, continuity, and connection in your parts work journey.",
);

export const action: ActionFunction = withCors(async ({ request }) => {
    const formData = await request.formData();
    const intent = formData.get("intent");

    if (intent === "anonymous-login") {
        const redirectTo = formData.get("redirectTo") || "/onboarding/terms";

        const { headers, supabase: serverClient } =
            createSupabaseServerClient(request);

        const { data, error } = await serverClient.auth.signInAnonymously();

        if (error) {
            return json(
                {
                    error: "Failed to create anonymous session. Please try again.",
                },
                { status: 400 },
            );
        }

        if (data?.user) {
            await ensureAnonymousProfile(data.user);
        }

        // AIDEV-NOTE: Analytics tracking handled client-side after successful redirect
        return redirect(redirectTo.toString(), { headers });
    }

    if (intent === "logout") {
        const { headers, supabase: serverClient } =
            createSupabaseServerClient(request);

        await serverClient.auth.signOut();

        // AIDEV-NOTE: Clear auth cookies and redirect - matches api.auth.logout behavior
        return redirect("/landing", { headers });
    }

    return json({ error: "Invalid intent" }, { status: 400 });
});

// AIDEV-NOTE: Anonymous visits logged server-side — client analytics miss ~80%
// of cold traffic (ad blockers). Authed views still come via /api/page-view.
export const loader: LoaderFunction = withCors(async ({ request }) => {
    await logAnonymousPageView(request);
    return json({});
});

export default function LandingRoute() {
    return <Landing />;
}

async function ensureAnonymousProfile(user: SupabaseUser) {
    if (!user?.id) {
        return;
    }

    const adminClient = getUnauditableSystemAdminSupabaseClient();

    try {
        const existingProfile = await profileRepository.getProfileById(
            user.id,
            adminClient,
        );

        if (existingProfile) {
            return;
        }

        // AIDEV-NOTE: Create barebones profile for anonymous users so protected routes don't bounce back to /landing
        await profileRepository.createProfile(
            {
                id: user.id,
                authUserId: user.id,
                email: user.email ?? null,
                onboarding_status: "not_started",
            },
            adminClient,
        );
    } catch (error) {
        const logger = getLogger().withMetadata({ anonymousUserId: user.id });
        logger
            .withError(error as Error)
            .error("Failed to ensure anonymous profile after signup");
        throw error;
    }
}
